"use client";
import { useEffect } from "react";
import { generateUUID } from "~/lib/generateUUID";
import { getClientInfo } from "~/lib/userAgentParser";
import type { BrowserLogEntry, LogEntryLevel } from "~/types";

/**
 * Detects if a user agent belongs to a bot/crawler.
 * @param {string} [ua] - Optional user agent string (defaults to browser's UA).
 * @returns {boolean} - True if a bot is detected.
 */
function isBot(ua = navigator.userAgent) {
  const botPattern =
    /bot|crawl|spider|slurp|google|bing|yandex|baidu|duckduck|lighthouse|insights|gptbot|anthropic/i;
  return botPattern.test(ua);
}


const sendError = (data: Record<string, unknown>) => {
  fetch("/report-error", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
    keepalive: true,
    credentials: "include",
  }).catch(() => { });
};

// ---------------------------------------------------------------------------
// Console log forwarding to /report-log
// ---------------------------------------------------------------------------

const logQueue: BrowserLogEntry[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;

// ---------------------------------------------------------------------------
// Trace ID — groups related log entries within a session window.
// A new ID is generated when:
//   • No log has been emitted for more than TRACE_WINDOW_MS (5 s), or
//   • The user taps/clicks (user input resets the session boundary).
// ---------------------------------------------------------------------------
const TRACE_WINDOW_MS = 5_000;
let currentTraceId: string = generateUUID();
let lastLogAt: number = 0;

const resetTraceId = () => {
  currentTraceId = generateUUID();
};

const getTraceId = (): string => {
  const now = Date.now();
  if (now - lastLogAt > TRACE_WINDOW_MS) {
    // Window expired — start a new trace
    currentTraceId = generateUUID();
  }
  lastLogAt = now;
  return currentTraceId;
};

// Module-level flag so console patching happens exactly once regardless of
// React re-mounts or hot module replacement.
let consolePatched = false;

const flushLogs = () => {
  if (!logQueue.length) return;
  const batch = logQueue.splice(0, logQueue.length);
  fetch("/report-log", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(batch),
    keepalive: true,
    credentials: "include",
  }).catch(() => { });
};

const safeSerialise = (a: unknown): unknown => {
  if (a instanceof Error) return { message: a.message, stack: a.stack };
  if (typeof a === "object" && a !== null) {
    try {
      return structuredClone(a);
    } catch {
      return String(a);
    }
  }
  return a;
};

const queueLog = (level: LogEntryLevel, args: unknown[], logStack?: string) => {
  const serialisedArgs = args.map(safeSerialise);

  // Separate primitive values (used to build message) from objects (spread to top level).
  const msgParts: string[] = [];
  const extraFields: Record<string, unknown> = {};

  for (const arg of serialisedArgs) {
    if (typeof arg === "string") {
      msgParts.push(arg);
    } else if (typeof arg === "boolean" || typeof arg === "number") {
      msgParts.push(String(arg));
    } else if (arg && typeof arg === "object" && !Array.isArray(arg)) {
      Object.assign(extraFields, arg);
    }
  }

  // Determine message: prefer concatenated strings; fall back to object.message.
  let msg: string;
  if (msgParts.length > 0) {
    msg = msgParts.join(" ");
  } else {
    const firstObj = serialisedArgs.find(
      (a) => a && typeof a === "object" && !Array.isArray(a),
    ) as Record<string, unknown> | undefined;
    if (firstObj && typeof firstObj.message === "string") {
      msg = firstObj.message;
    } else {
      msg = serialisedArgs.length ? String(serialisedArgs[0]) : "";
    }
  }

  logQueue.push({
    level,
    msg,
    ts: new Date().toISOString(),
    path: window.location.pathname + window.location.search,
    traceId: getTraceId(),
    ...(logStack ? { logStack } : {}),
    ...extraFields,
    ...getClientInfo(),
  });

  // Flush after 1 second of inactivity; send immediately if queue is large.
  if (logQueue.length >= 20) {
    if (flushTimer !== null) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    flushLogs();
  } else if (flushTimer === null) {
    flushTimer = setTimeout(() => {
      flushTimer = null;
      flushLogs();
    }, 1000);
  }
};

/** Returns the un-patched console.log, falling back to the current one. */
const origLog = (): typeof console.log =>
  (window as unknown as { __origConsoleLog?: typeof console.log })
    .__origConsoleLog ?? console.log;

// Resets the trace ID on user interaction so each gesture starts a fresh trace.
// Defined at module scope so its reference is stable across renders.
const onUserInput = () => {
  resetTraceId();
};

const LogCollector = () => {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // ---- unhandled errors & rejections ----
    const onError = (event: ErrorEvent) => {
      origLog()("Captured unhandled error", { event });
      sendError({
        msg: event.message,
        stack: event.error?.stack,
        traceId: (event.error as { traceId?: string } | undefined)?.traceId,
        path: window.location.pathname + window.location.search,
      });
    };

    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      origLog()("Captured unhandled promise rejection", { event });
      const reason = event.reason as
        | { message?: string; stack?: string; traceId?: string }
        | undefined;
      sendError({
        msg: reason?.message ?? "Promise rejected",
        stack: reason?.stack,
        traceId: reason?.traceId,
        path: window.location.pathname + window.location.search,
      });
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("click", onUserInput, {
      capture: true,
      passive: true,
    });
    window.addEventListener("touchstart", onUserInput, {
      capture: true,
      passive: true,
    });

    // ---- console method patching (once per page load) ----
    if (!consolePatched) {
      consolePatched = true;
      const isDev =
        typeof process !== "undefined" &&
        process.env &&
        process.env.NODE_ENV === "development";

      // Skip console patching for bot/crawler user agents.
      const isBotAgent = isBot();

      if (isDev) {
        // In development avoid overriding console so DevTools behave normally.
        origLog()("Skipping console patching in development environment");
      } else if (isBotAgent) {
        // Do not collect logs from bots/crawlers.
      } else {
        const methods: LogEntryLevel[] = [
          "log",
          "info",
          "warn",
          "error",
          "debug",
        ];
        const originals: Partial<Record<LogEntryLevel, typeof console.log>> =
          {};

        methods.forEach((method) => {
          originals[method] = console[method].bind(console);
          // Expose originals on window so other modules can bypass the patch.
          (window as unknown as Record<string, unknown>)[
            `__origConsole${method.charAt(0).toUpperCase() + method.slice(1)}`
          ] = originals[method];

          console[method] = (...args: unknown[]) => {
            // Call original so logs still appear in browser DevTools.
            originals[method]!(...args);
            // Capture a stack trace so the server can pinpoint the caller.
            const logStack = new Error().stack ?? "";
            // Forward to backend.
            queueLog(method, args, logStack);
          };
        });

        // Flush remaining entries before page unloads.
        window.addEventListener("pagehide", flushLogs);
        window.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "hidden") flushLogs();
        });
      }
    }

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("click", onUserInput, { capture: true });
      window.removeEventListener("touchstart", onUserInput, { capture: true });
    };
  }, []);

  return null;
};

export default LogCollector;
